import { Button, Divider } from '@mui/material';
import { IEventParticipantRepository } from 'domain/event/participants/EventParticipant.repository';
import { EventParticipantStatusEnum } from 'domain/event/participants/EventParticipantStatusEnum';
import { KeyValue } from 'primary/Components/KeyValue/KeyValue';
import { ActionButtonsForContent } from 'primary/MessageList/contents/ActionButtonsForContent';
import {
  getNavigationLinkFromRessourceKey,
  parseRessourceFromNotificaton,
} from 'primary/MessageList/NotificationListItem';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationResourceTypeEnum } from '../../../domain/notification/NotificationResourceTypeEnum';
import { UnpyNotification } from '../../../domain/notification/UnpyNotification';
import {
  TranslationOption,
  TranslationType,
} from '../../../domain/translation/Translation.repository';
import { useContextDependency } from '../../hooks/useContextDependency';
import { useRetrieveFromDomain } from '../../hooks/useRetrieveFromDomain';
import { useTranslate } from '../../hooks/useTranslate';

interface NotificationParticipationEventContentProps {
  notification: UnpyNotification;
  reFetchNotification: () => void;
  closePreview?: () => void;
}

export const getOnClickResponseEventParticipation = (
  eventParticipantRepository: IEventParticipantRepository,
  t: (key: TranslationType, option?: TranslationOption) => string,
  response: boolean,
  reFetchNotification: () => void,
  teamId: number,
  eventId: number,
  setLoading: (loading: boolean) => void,
) => {
  return () => {
    setLoading(true);
    eventParticipantRepository
      .responseParticipationEvent(response, teamId, eventId)
      .then(() => {
        reFetchNotification();
        setLoading(false);
      });
  };
};

export const NotificationParticipationEventContent = ({
  notification,
  reFetchNotification,
  closePreview,
}: NotificationParticipationEventContentProps) => {
  const t = useTranslate();
  const { eventParticipantRepository } = useContextDependency();
  const [loading, setLoading] = React.useState(false);
  const [eventParticipant, , fetching] = useRetrieveFromDomain(
    () =>
      eventParticipantRepository.getEventParticipantById(
        parseInt(
          notification.resources[NotificationResourceTypeEnum.EVENT_PARTICIPANT_ID],
        ),
      ),
    undefined,
  );

  const ressourcesParsed = parseRessourceFromNotificaton(notification);
  const navigate = useNavigate();
  if (!eventParticipant) return null;

  return (
    <div>
      <div className={'-keyValueContainerNotification'}>
        {ressourcesParsed
          .filter(
            (r) => !r.isLink && r.keyValue !== NotificationResourceTypeEnum.TEAM_NAME,
          )
          .map((res) => (
            <KeyValue
              key={res.keyValue}
              accent
              keyValue={t(
                `notificationList.item.ressourceLabelByKey.${
                  res.keyValue as NotificationResourceTypeEnum
                }`,
              )}
              value={res.value}
            />
          ))}
      </div>
      <div className={'-linkContainerNotification'}>
        {ressourcesParsed
          .filter((r) => r.isLink)
          .map((res) => (
            <Button
              key={res.keyValue}
              variant={'outlined'}
              className={'-linkNotification'}
              onClick={() => {
                const link = getNavigationLinkFromRessourceKey(
                  res.keyValue as NotificationResourceTypeEnum,
                  res.value,
                  notification.resources,
                  notification.type,
                );
                navigate(link);
                closePreview?.();
              }}
            >
              {t(
                `notificationList.item.ressourceLabelByKey.${
                  res.keyValue as NotificationResourceTypeEnum
                }`,
              )}
            </Button>
          ))}
      </div>
      <Divider className={'-topMarged'} />
      {eventParticipant.status === EventParticipantStatusEnum.REJECTED && (
        <span>{t('notification.content.joinEvent.alreadyRefused')}</span>
      )}
      {eventParticipant.status === EventParticipantStatusEnum.REGISTERED && (
        <span>{t('notification.content.joinEvent.alreadyAccepted')}</span>
      )}
      {eventParticipant &&
        eventParticipant.status === EventParticipantStatusEnum?.WAIT_VALIDATE && (
          <ActionButtonsForContent
            loading={loading}
            onClickCancel={getOnClickResponseEventParticipation(
              eventParticipantRepository,
              t,
              false,
              reFetchNotification,
              eventParticipant.id,
              parseInt(notification.resources[NotificationResourceTypeEnum.EVENT_ID]),
              setLoading,
            )}
            onClickValid={getOnClickResponseEventParticipation(
              eventParticipantRepository,
              t,
              true,
              reFetchNotification,
              eventParticipant.id,
              parseInt(notification.resources[NotificationResourceTypeEnum.EVENT_ID]),
              setLoading,
            )}
          />
        )}
      {!fetching && !eventParticipant && (
        <span>{t('notification.content.joinEvent.alreadyAccepted')}</span>
      )}
    </div>
  );
};
